// The attachment row's stylesheet (popoverFiles.css): comment files render the shared FileRow.
import '@overlayqa/shared/ui/styles.css';
import { PALETTE } from '@overlayqa/shared';
/**
 * The comment composer: the input box with its transparent-text overlay
 * (mentions highlighted on top of the real textarea), the @mention dropdown,
 * and the send button. Extracted from CommentThread.tsx (refactor phase C4,
 * docs/refactoring/plans/2026-09-08-commentthread.md); the markup is the
 * thread's former composer block verbatim, with handleSubmit becoming the
 * onSubmit prop and createComment.isPending the isPending prop. The
 * autocomplete pieces arrive under the names the markup already used, typed
 * off useMentionAutocomplete so the two cannot drift; the thread still owns
 * the draft (content / setContent), the submit and the textarea ref.
 *
 * The keydown lets autocomplete consume the key first. Enter submits only
 * while no list is open, Shift is up and no post is in flight; the send
 * button disables on a blank draft or a post in flight.
 */
import type React from 'react';
import { CommentAttachmentPicker, CommentEmojiPicker, useTextareaAutoHeight } from '@overlayqa/shared/ui';
import type { CommentAttachmentUpload } from '@overlayqa/shared';
import { SentIcon } from '@hugeicons/core-free-icons';
import { Button } from '../ui/button';
import { COMMENT_VISIBILITY_OPTIONS, commentVisibilityHint, type CommentVisibility, type MentionableUser } from '@overlayqa/shared';
import { SelectDropdown } from '../shared/SelectDropdown';
import { renderInputContent } from './commentMentions';
import type { useMentionAutocomplete } from './useMentionAutocomplete';

type Autocomplete = ReturnType<typeof useMentionAutocomplete>;

export interface CommentComposerProps {
  attachments?: CommentAttachmentUpload[];
  onAttachmentsChange?: (files: CommentAttachmentUpload[]) => void;
  readingFiles?: boolean;
  onReadingFilesChange?: (reading: boolean) => void;
  content: string;
  setContent: (v: string) => void;
  teamMembers: MentionableUser[];
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  mentionQuery: Autocomplete['mentionQuery'];
  filteredMembers: Autocomplete['filteredMembers'];
  activeIndex: Autocomplete['activeIndex'];
  insertMention: Autocomplete['insertMention'];
  handleTextChange: Autocomplete['handleTextChange'];
  handleMentionKeyDown: Autocomplete['handleKeyDown'];
  onSubmit: () => void;
  isPending: boolean;
  visibility: CommentVisibility;
  onVisibilityChange: (value: CommentVisibility) => void;
}

export function CommentComposer({
  attachments = [], onAttachmentsChange, readingFiles = false, onReadingFilesChange,
  content,
  setContent,
  teamMembers,
  textareaRef,
  mentionQuery,
  filteredMembers,
  activeIndex,
  insertMention,
  handleTextChange,
  handleMentionKeyDown,
  onSubmit,
  isPending,
  visibility,
  onVisibilityChange,
}: CommentComposerProps) {
  useTextareaAutoHeight(textareaRef, content);

  return (
    <>
      {/* Input — no extra horizontal padding; the card's p-5 already provides
          the 20px inset so the input box lines up with the header and list. */}
      <div className="relative py-3">
        {mentionQuery !== null && filteredMembers.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto z-20 p-1">
            {filteredMembers.map((member, i) => (
              <div
                key={member.id}
                className={`flex items-center gap-2 px-2 py-2 rounded text-xs cursor-pointer ${i === activeIndex ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(member, content, setContent);
                }}
              >
                <span className="font-medium">{member.name}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end border border-gray-200 rounded-lg focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-200">
          <div className="relative flex-1 self-center min-w-0">
            {/* Styled overlay — mirrors textarea text with mention highlights */}
            {content && (
              <div
                className="absolute inset-0 p-2 text-xs whitespace-pre-wrap break-words pointer-events-none overflow-hidden"
                style={{ lineHeight: '1.25rem' }}
                aria-hidden="true"
              >
                {renderInputContent(content, teamMembers)}
              </div>
            )}
            <textarea
              ref={textareaRef}
              className="block w-full p-2 text-xs resize-none outline-none bg-transparent overflow-hidden"
              style={{ color: 'transparent', caretColor: PALETTE.neutral[700], lineHeight: '1.25rem' }}
              value={content}
              onChange={(e) => handleTextChange(e, setContent)}
              onKeyDown={(e) => {
                if (handleMentionKeyDown(e, content, setContent)) return;
                if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
                  e.preventDefault();
                  if (!isPending && !readingFiles) onSubmit();
                }
              }}
              placeholder="Add a comment..."
              rows={1}
            />
          </div>
          <Button
            type="button"
            context="compact"
            iconOnly
            icon={SentIcon}
            className="flex-shrink-0 m-2"
            onClick={onSubmit}
            disabled={(!content.trim() && !attachments.length) || isPending || readingFiles}
            aria-label="Send comment"
          />
        </div>
        {onAttachmentsChange ? <CommentAttachmentPicker value={attachments} onChange={onAttachmentsChange} disabled={isPending} onReadingChange={onReadingFilesChange} buttonClassName="btn-secondary btn-sm">
          <CommentEmojiPicker textareaRef={textareaRef} onChange={setContent} disabled={isPending} />
        </CommentAttachmentPicker> : <CommentEmojiPicker textareaRef={textareaRef} onChange={setContent} disabled={isPending} />}
        {/* No "Visible to" words (Emily, 2026-11-03): the picker's value and the hint below say the audience. */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <SelectDropdown
            value={visibility}
            onChange={(value) => onVisibilityChange(value as CommentVisibility)}
            options={COMMENT_VISIBILITY_OPTIONS}
            variant="ghost"
            aria-label="Comment visibility"
            disabled={isPending}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">{commentVisibilityHint(visibility)}</p>
      </div>
    </>
  );
}
